import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { BoardExtendedData } from '../../../../api/ApiTypes';
import { Input, Menu } from '../../../../ui';
import { LoadingGuard } from '../../../../ui/LoadingGuard';
import { MenuButton } from '../../../../ui/MenuButton';
import { PageButtonStrip } from '../../../../ui/PageButtonStrip';
import { useLocationScope } from '../../../AppLocation';
import { useResolve } from '../../../Container';
import { SortMode } from '../../../common/Sorter';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { useOnBulkArchiveBoards, useOnBulkDeleteBoards, useOnBulkMoveBoards } from '../AdminActions';
import { ManageFrame } from '../ManageFrame';
import { BoardTableView } from './BoardTableView';

const BulkActionMenu = ({ selection }: { selection: BoardExtendedData[] }) => {

    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const boardIds = useMemo(() => selection.map(x => x.id), [selection]);
    const onBulkMoveBoards = useOnBulkMoveBoards(boardIds, setClosed);
    const onBulkArchiveBoards = useOnBulkArchiveBoards(boardIds, setClosed);
    const onBulkDeleteBoards = useOnBulkDeleteBoards(boardIds, setClosed);

    const label = !!selection.length
        ? `Bulk Actions (${selection.length})`
        : 'Bulk Actions';

    return <MenuButton disabled={!selection.length} label={label} {...{ open, setOpen }}>
        <Menu>
            <Menu.Section first>
                <Menu.Item onClick={onBulkMoveBoards}>
                    <Menu.ItemText>
                        Move Boards
                    </Menu.ItemText>
                </Menu.Item>
                <Menu.Item onClick={onBulkArchiveBoards}>
                    <Menu.ItemText>
                        Archive Boards
                    </Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
            <Menu.Section>
                <Menu.Item onClick={onBulkDeleteBoards}>
                    <Menu.ItemText>
                        Delete Boards
                    </Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
        </Menu>
    </MenuButton>;
};

const useBoardQuery = (search: string, sort: SortMode, startAt: number) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['admin.boards.list', search, startAt, ...sort],
        queryFn: async () => api.exec(v2.admin.boards.list(search, sort.join(':'), startAt, 50)),
    });
};

export const ManageBoardsView = () => {

    useLocationScope('boards', 'overview');

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState(['createdAt', 'desc'] as SortMode);
    const [selection, setSelection] = useState([] as BoardExtendedData[]);
    const [startAt, setStartAt] = useState(0);
    const { data, isLoading } = useBoardQuery(search, sort, startAt);

    // Reset startAt on search
    useEffect(() => {
        setStartAt(0);
    }, [search]);

    return <ManageFrame title="Boards">
        <Flexer flex="row" gap="1" align="center" padding="1 0">
            <Flexer shrink>
                <BulkActionMenu selection={selection} />
            </Flexer>
        </Flexer>
        <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
            <Flexer width="16">
                <Input placeholder="Search" value={search} onChange={setSearch} />
            </Flexer>
            <Flexer shrink>
                <PageButtonStrip data={data} onPageChange={setStartAt} />
            </Flexer>
        </Flexer>
        <LoadingGuard loading={isLoading}>
            {/* {data && <DataTable table={table} selectFromFirstCellOnly theme={TaskTableTheme} />} */}
            {data && <BoardTableView items={data.items} onSortChange={setSort} onSelectionChange={setSelection} />}
        </LoadingGuard>
    </ManageFrame>;
};
